import { Card, Colors, FOCUS_RING, Icon, Text, Typography } from '@amelio/ui-library';

import { RadioMarker } from './RadioMarker';

import type { RovingRadioProps } from '@/hooks/useRovingRadioGroup';
import type { TranslationKey } from '@/i18n-key';
import type { MeetingTemplate, ThematicMeetingTopic } from '@/types/thematic-meetings';

import { useGetMeetingTemplates } from '@/api/thematic-meetings';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { THEMATIC_MEETING_TOPIC_LABEL_KEYS } from '@/constants/ThematicMeetingTopic';
import { useAmelioTranslations } from '@/hooks';
import { useRovingRadioGroup } from '@/hooks/useRovingRadioGroup';

interface TopicTilesProps {
    selectedTopic: ThematicMeetingTopic | null;
    onSelectTopic: (topic: ThematicMeetingTopic) => void;
}

interface TopicTileProps {
    template: MeetingTemplate;
    radioProps: RovingRadioProps;
}

// A tile names a topic, not a plan: both its name and its description are ours to translate.
const TOPIC_DESCRIPTION_KEYS: Record<ThematicMeetingTopic, TranslationKey> = {
    Skill: 'thematicMeetings.newMeeting.topics.skill.description',
    Recurring: 'thematicMeetings.newMeeting.topics.recurring.description',
    Goals: 'thematicMeetings.newMeeting.topics.goals.description',
    Feedback: 'thematicMeetings.newMeeting.topics.feedback.description',
    Career: 'thematicMeetings.newMeeting.topics.career.description',
    Recognition: 'thematicMeetings.newMeeting.topics.recognition.description',
    Workload: 'thematicMeetings.newMeeting.topics.workload.description',
    Team: 'thematicMeetings.newMeeting.topics.team.description',
    Informal: 'thematicMeetings.newMeeting.topics.informal.description',
};

const TopicTile = ({ template, radioProps }: TopicTileProps) => {
    const { t } = useAmelioTranslations();
    const { topic } = template;
    const isSelected = radioProps['aria-checked'];
    const title = t(THEMATIC_MEETING_TOPIC_LABEL_KEYS[topic]);
    const description = t(TOPIC_DESCRIPTION_KEYS[topic]);

    // `Card interactive` renders a <button>: putting the radio inside it would nest interactive
    // controls, and ARIA flattens every descendant of a button — the radio role and its checked
    // state would never reach a screen reader. The card is a plain surface and the radio itself is
    // the one focusable, clickable node of the tile.
    return (
        <Card
            padding={'none'}
            borderRadius={'2xl'}
            borderWidth={'default'}
            borderColor={isSelected ? Colors.PRIMARY : Colors.BASE_100}
            bgColor={isSelected ? 'bg-primary-50' : 'bg-white'}
            className={'relative h-full'}
        >
            <div
                aria-hidden={'true'}
                className={'pointer-events-none absolute top-[16px] right-[16px]'}
            >
                <RadioMarker
                    isSelected={isSelected}
                    dataTestId={`topic-tile-${topic}-indicator`}
                />
            </div>

            <div
                {...radioProps}
                aria-label={title}
                data-testid={`topic-tile-${topic}`}
                // The radio carries the padding and the hover, so the whole tile answers the pointer and
                // the focus ring follows the tile's edge.
                className={`${FOCUS_RING} hover:bg-primary-50 flex h-full cursor-pointer flex-col gap-[4px] rounded-2xl p-[16px] pr-[48px] transition-colors`}
            >
                <Text weight={'bold'}>{title}</Text>

                {/* One step below the title on the scale, and a step lighter: at the same size the
                    description read as a second title, only less bold. */}
                <Typography
                    variant={'caption'}
                    color={Colors.BASE_400}
                >
                    {description}
                </Typography>
            </div>
        </Card>
    );
};

interface TopicTileGroupProps {
    templates: MeetingTemplate[];
    getRadioProps: (topic: ThematicMeetingTopic) => RovingRadioProps;
}

const TopicTileGroup = ({ templates, getRadioProps }: TopicTileGroupProps) => (
    <div className={'grid grid-cols-1 gap-[16px] sm:grid-cols-2'}>
        {templates.map((template) => (
            <TopicTile
                key={template.topic}
                template={template}
                radioProps={getRadioProps(template.topic)}
            />
        ))}
    </div>
);

// A company may hold several plans for one topic, and these tiles choose the topic, not the plan: the
// topic's first plan places it (popularity, position), and a topic left with no plan has no tile.
const firstPlanOfEachTopic = (plans: MeetingTemplate[]): MeetingTemplate[] =>
    plans.filter((plan, index) => plans.findIndex(({ topic }) => topic === plan.topic) === index);

/**
 * Meeting-topic radiogroup. The tiles form ONE radiogroup — the choice is exclusive across both
 * visual groups — and follow the WAI-ARIA radiogroup pattern: a roving tabindex, arrow keys that
 * move focus and selection, Space/Enter to select the focused tile. Which topics are offered, and
 * the popular/other split, come from the company's plans; what a topic is called is ours.
 */
export const TopicTiles = ({ selectedTopic, onSelectTopic }: TopicTilesProps) => {
    const { t } = useAmelioTranslations();
    const { data: templates, isLoading } = useGetMeetingTemplates();

    const topicPlans = firstPlanOfEachTopic(templates ?? []);
    const orderedTopics = topicPlans.map((template) => template.topic);
    const popularTemplates = topicPlans.filter((template) => template.isPopular);
    const otherTemplates = topicPlans.filter((template) => !template.isPopular);

    const { getRadioProps } = useRovingRadioGroup({
        options: orderedTopics,
        selected: selectedTopic,
        onSelect: onSelectTopic,
    });

    return (
        <div
            role={'radiogroup'}
            aria-label={t('thematicMeetings.newMeeting.topicsGroupLabel')}
            className={'flex flex-col gap-[32px]'}
            data-testid={'topic-tiles'}
        >
            <OverlayLoader isLoading={isLoading}>
                <div className={'flex flex-col gap-[32px]'}>
                    <div className={'flex flex-col gap-[16px]'}>
                        <div className={'flex items-center gap-2'}>
                            <Icon
                                name={'support-star-1'}
                                size={'md'}
                                color={Colors.PRIMARY}
                                ariaHidden
                            />

                            <Typography
                                variant={'overline'}
                                weight={'bold'}
                            >
                                {t('thematicMeetings.newMeeting.mostPopularTopics')}
                            </Typography>
                        </div>

                        <TopicTileGroup
                            templates={popularTemplates}
                            getRadioProps={getRadioProps}
                        />
                    </div>

                    <div className={'flex flex-col gap-[16px]'}>
                        <Typography
                            variant={'overline'}
                            weight={'bold'}
                        >
                            {t('thematicMeetings.newMeeting.otherTopics')}
                        </Typography>

                        <TopicTileGroup
                            templates={otherTemplates}
                            getRadioProps={getRadioProps}
                        />
                    </div>
                </div>
            </OverlayLoader>
        </div>
    );
};
